import {
  ALLOWED_TAG_NAMES,
  DEFAULT_ALLOWED_DECIMAL_SEPARATORS,
  DEFAULT_ALLOWED_THOUSANDS_SEPARATORS,
  DEFAULT_DECIMAL_SEPARATOR,
  DEFAULT_THOUSANDS_SEPARATOR,
  DIGITS,
} from './constants';
import { SimpleProducer, EnhancedProducer, ExtraStylesConfig, AllowedTagName } from './types';

export const isDigit = (str: string) => DIGITS.includes(str);

export const getExtraStyles = (input: ExtraStylesConfig) => {
  const extraStyles: React.CSSProperties = {};
  if (input.isBold) extraStyles.fontWeight = 'bold';
  if (input.isItalic) extraStyles.fontStyle = 'italic';
  if (input.isUnderlined) extraStyles.textDecoration = 'underline';
  if (input.textColor) extraStyles.color = input.textColor;
  if (input.backgroundColor) extraStyles.backgroundColor = input.backgroundColor;
  if (input.textAlign) extraStyles.textAlign = input.textAlign;
  return extraStyles;
};

export const getStyleForSubstring = (
  f: SimpleProducer<React.CSSProperties> | undefined,
  index: number,
  str: string,
) => (f !== undefined ? f(index, str) ?? {} : {});

export const getStyleForDetectedNumber = (
  f: EnhancedProducer<React.CSSProperties> | undefined,
  index: number,
  str: string,
  num: number,
  formattedNum: string,
) => (f !== undefined ? f(index, str, num, formattedNum) ?? {} : {});

export function getPreviousIndices<T>(prevArray: T[], newArray: T[]) {
  let searchAfter = -1;
  const output: number[] = [];
  for (let i = 0; i < newArray.length; i += 1) {
    const x = prevArray.indexOf(newArray[i], searchAfter + 1);
    output.push(x);
    if (x >= 0) searchAfter = x;
  }
  return output;
}

// ===== validators =====

export const validateDecimalSeparator = (str: string | undefined) =>
  str === undefined || [...str].length !== 1 ? DEFAULT_DECIMAL_SEPARATOR : str;

export const validateThousandsSeparator = (str: string | undefined) =>
  str ?? DEFAULT_THOUSANDS_SEPARATOR;

export const validateDecimalPlaces = (n: number | undefined) =>
  n === undefined || n < 0 || n > 20 ? 2 : n;

export const validateAllowedTagNames = (stringifiedTagNames: string) => {
  const tagNames: AllowedTagName[] = JSON.parse(stringifiedTagNames);
  return tagNames.length === 0 ? ALLOWED_TAG_NAMES : tagNames;
};

export const validateAllowedDecimalSeparators = (stringifiedDecimalSeparators: string) => {
  const decimalSeparators: string[] = JSON.parse(stringifiedDecimalSeparators);
  return decimalSeparators.length === 0
    ? DEFAULT_ALLOWED_DECIMAL_SEPARATORS
    : [...new Set(decimalSeparators.map((x) => validateDecimalSeparator(x)))]; // using [... new Set(array)] to remove duplicates
};

export const validateAllowedThousandsSeparators = (stringifiedThousandsSeparators: string) => {
  const thousandsSeparators: string[] = JSON.parse(stringifiedThousandsSeparators);
  return thousandsSeparators.length === 0
    ? DEFAULT_ALLOWED_THOUSANDS_SEPARATORS
    : thousandsSeparators;
};

/**
 * Returns an array of length `length`
 */
export function validateArrayLength<T>(arr: T[] | undefined, length: number, fill: T) {
  let result: T[];
  if (arr === undefined) {
    result = Array(length).fill(fill);
  } else if (arr.length === length) {
    result = arr;
  } else if (arr.length > length) {
    result = arr.slice(0, length);
  } else {
    result = arr.concat(Array(length - arr.length).fill(fill));
  }
  return result;
}

export const validate01StringLength = (str: string | undefined, length: number) => {
  if (str === undefined) {
    return '0'.repeat(length);
  }
  if (str.length === length) {
    return str;
  }
  if (str.length > length) {
    return str.slice(0, length);
  }
  return str + '0'.repeat(length - str.length);
};
